import api from '@/libs/axiosInstance';
import type { ApiResponse } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { trackProductAction } from '@/libs/product-analytics';

interface AcceptTeamInvitationRequest {
  token: string;
  password?: string;
  firstName?: string;
  lastName?: string;
}

interface AcceptTeamInvitationSuccessResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  } | null;
  organization: {
    id: string;
    name: string;
  } | null;
  token: {
    type: string;
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  } | null;
  isNewUser: boolean;
  requiresLogin?: false;
}

interface AcceptTeamInvitationRequiresLoginResponse {
  requiresLogin: true;
  email: string;
}

export type AcceptTeamInvitationResponse =
  | AcceptTeamInvitationSuccessResponse
  | AcceptTeamInvitationRequiresLoginResponse;

interface ApiErrorResponse {
  message: string;
  code?: string;
}

const acceptTeamInvitation = async (
  params: AcceptTeamInvitationRequest
): Promise<AcceptTeamInvitationResponse> => {
  const { token, ...body } = params;
  const response = await api.post<ApiResponse<AcceptTeamInvitationResponse>>(
    `/team/invitations/${token}/accept`,
    body
  );

  if (response.data.success && response.data.data) {
    return response.data.data;
  }

  throw new Error(response.data.message || 'Failed to accept invitation');
};

export const useAcceptTeamInvitation = () => {
  return useMutation<
    AcceptTeamInvitationResponse,
    AxiosError<ApiErrorResponse>,
    AcceptTeamInvitationRequest
  >({
    mutationFn: (input) => trackProductAction('team', 'accept', () => acceptTeamInvitation(input)),
  });
};
